import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Box, Typography } from '@mui/material';

// --- Configuration Interfaces and Defaults ---

interface IntensityData {
  timestamps: string[];
  values: { [googlePlaceId: string]: number[] };
}

interface ColorThreshold {
  threshold: number;
  color: string;
  label: string;
  textColor?: string;
}

const DEFAULT_COLOR_CONFIG: ReadonlyArray<ColorThreshold> = [
  { threshold: 0.9, color: '#FFD859', label: 'Full Sun', textColor: '#000000' },
  { threshold: 0.5, color: '#F3A712', label: 'Mostly Sun', textColor: '#000000' },
  { threshold: 0.1, color: '#8F6D32', label: 'Partial Shade', textColor: '#FFFFFF' },
  { threshold: 0.0001, color: '#174378', label: 'Full Shade', textColor: '#FFFFFF' },
  { threshold: 0.0, color: '#000000', label: 'Night', textColor: '#FFFFFF' },
];

// --- Globally Applied Constants (as per request) ---
const MIN_BAR_VISUAL_INTENSITY = 0.1; // Applied to all modes: Min perceived height for a 0% actual intensity bar.
const GLOBAL_X_AXIS_AREA_HEIGHT_PX = 10; // Applied to all modes: Height of the X-axis area.
const GLOBAL_TOOLTIP_PILL_FONT_SIZE_PX = 12; // Applied to all modes: Font size for main text in tooltip.
const GLOBAL_TOOLTIP_PILL_TIME_FONT_SIZE_PX = 10; // Applied to all modes: Font size for time in tooltip.
const GLOBAL_X_AXIS_FONT_SIZE_PX = 10; // Font size for X-axis tick labels.


// --- Base Layout Constants (used as defaults for 'full' mode) ---
const BASE_TOOLTIP_OFFSET_ABOVE_CHART_AREA_PX = 20; // Default: How far above chart container pill bottom sits.
const BASE_INDICATOR_CIRCLE_SIZE_PX = 18;          // Default: Diameter of the colored circle indicator.
const BASE_INDICATOR_CIRCLE_BORDER_PX = 4;         // Default: Border width of the circle indicator.
const BASE_INDICATOR_CIRCLE_Y_OFFSET_PX = -1;      // Default: Vertical offset for the circle.
const BASE_CHART_TOP_MARGIN = 5;                   // Default: Top margin for the chart area.

const TOOLTIP_STALK_WIDTH_PX = 4;                  // Width of the vertical stalk.
const TOOLTIP_PILL_VERTICAL_PADDING_PX = 4;        // Internal vertical padding of the tooltip pill.
const CHART_MARGIN_LEFT = 10;                      // Left margin for the chart.
const CHART_MARGIN_RIGHT = 10;                     // Right margin for the chart.
const CHART_BOTTOM_MARGIN = 5;                     // BarChart's margin.bottom.

// --- Bar Styling Constants ---
const BAR_TOP_RADIUS_PX = 8;                       // Radius for the top corners of bars.
const BAR_CATEGORY_GAP_PERCENT = 10;              // Gap between bar categories (negative makes bars wider).


// --- Data Interfaces ---
interface TransformedDataPoint {
  index: number;
  originalTimestamp: string;
  time: string;
  actualIntensity: number;
  displayIntensity: number;
  color: string;
  label: string;
  textColor?: string;
  isPadding?: boolean;
}

interface ActiveTooltipInfo {
  point?: TransformedDataPoint;
  xPos: number;
  displayTime: string;
}

interface IntensityTimelineProps {
  data: IntensityData | Record<string, never>; // The raw intensity data from the API.
  googlePlaceId: string;                       // Identifier for the specific dataset to use from `data.values`.
  loading?: boolean;                           // If true, shows a loading shimmer.
  onTooltipActive?: (isActive: boolean, dataPoint?: TransformedDataPoint) => void; // Callback when tooltip becomes active/inactive.
  mode?: 'full' | 'compact';                   // Mode to control layout and some data params. Defaults to 'full'.
  startTime?: string;                          // Start time for the timeline view (HH:MM format). Overridden by compact mode if `mode` prop is set.
  endTime?: string;                            // End time for the timeline view (HH:MM format). Overridden by compact mode if `mode` prop is set.
  resolutionMinutes?: number;                  // The time duration each bar represents in minutes. Overridden by compact mode if `mode` prop is set.
  sunriseTime?: string;                        // Sunrise time from API (HH:MM:SS.fraction format), used for dawn/dusk visuals.
  sunsetTime?: string;                         // Sunset time from API (HH:MM:SS.fraction format), used for dawn/dusk visuals.
  duskDawnDurationMinutes?: number;            // Duration of the dawn/dusk periods in minutes. Defaults to 30.
  colorConfig?: ReadonlyArray<ColorThreshold>; // Configuration for mapping intensity values to colors and labels.
  tickIntervalHours?: number;                  // Interval for X-axis time ticks in hours. Defaults to 4.
  barStrokeWidth?: number;                     // Width of the stroke around each bar. Defaults to 1.
}

// --- Helper Functions ---
const timeToMinutes = (timeStr: string | undefined): number => {
  if (!timeStr?.includes(':')) return 0; const parts = timeStr.split(':').map(Number); return (parts[0] || 0) * 60 + (parts[1] || 0);
};

const minutesToHHMM = (totalMinutes: number, clampToDay: boolean = true): string => {
  let h = Math.floor(totalMinutes / 60); const m = Math.round(totalMinutes % 60); if (clampToDay) h %= 24; return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const dateToMinutes = (date: Date): number => date.getHours() * 60 + date.getMinutes();

const formatTimeToHHMM = (isoOrTimeStr: string): string => {
  if (typeof isoOrTimeStr === 'string' && isoOrTimeStr.match(/^\d{2}:\d{2}$/)) return isoOrTimeStr;
  try { const d = new Date(isoOrTimeStr); if (isNaN(d.getTime())) throw Error(); return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }); } catch {
    if (typeof isoOrTimeStr === 'string') { const p = isoOrTimeStr.split(':'); if (p.length >= 2 && !isNaN(parseInt(p[0])) && !isNaN(parseInt(p[1]))) return `${String(p[0]).padStart(2, '0')}:${String(p[1]).padStart(2, '0')}`; } return '--:--';
  }
};

const getColorAndLabelConfig = (intensity: number, config: ReadonlyArray<ColorThreshold>): Omit<TransformedDataPoint, 'originalTimestamp' | 'time' | 'actualIntensity' | 'displayIntensity' | 'index' | 'isPadding'> => {
  const sortedConfig = [...config].sort((a, b) => b.threshold - a.threshold); const zeroItem = sortedConfig.find(i => i.threshold === 0.0);
  if (intensity > 0) { const match = sortedConfig.find(i => intensity >= i.threshold && i.threshold > 0.0); if (match) return { color: match.color, label: match.label, textColor: match.textColor }; const lowPos = sortedConfig.filter(c => c.threshold > 0).pop(); if (lowPos) return { color: lowPos.color, label: lowPos.label, textColor: lowPos.textColor }; }
  if (zeroItem) return { color: zeroItem.color, label: zeroItem.label, textColor: zeroItem.textColor }; return { color: '#CCCCCC', label: 'Unknown', textColor: '#000000' };
};

const processChartData = (rawData: IntensityData | Record<string, never>, googlePlaceId: string, viewStartTime: string, viewEndTime: string, resolutionMins: number, colorConf: ReadonlyArray<ColorThreshold>): TransformedDataPoint[] => {
  const rawPV = rawData?.values?.[googlePlaceId] || rawData?.values?.[Object.keys(rawData.values || {})[0]]; const rawTS = rawData?.timestamps; const hasValid = rawTS?.length && rawPV && rawPV.length === rawTS.length;
  const finalPoints: TransformedDataPoint[] = []; const startM = timeToMinutes(viewStartTime); const endM = timeToMinutes(viewEndTime); let pIdx = 0;
  const { color: zC, label: zL, textColor: zT } = getColorAndLabelConfig(0, colorConf); const rawMap = new Map<number, { sumI: number, cnt: number, firstTS: string }>();
  if (hasValid) { for (let i = 0; i < rawTS.length; i++) { const ts=rawTS[i], inten=rawPV[i]||0, minOfDay=timeToMinutes(formatTimeToHHMM(ts)); if (rawMap.has(minOfDay)) { const ex=rawMap.get(minOfDay)!; ex.sumI+=inten; ex.cnt++; } else { rawMap.set(minOfDay, { sumI: inten, cnt: 1, firstTS: ts }); } } }
  for (let slotStartM = startM; slotStartM < endM; slotStartM += resolutionMins) {
    const slotTimeS = minutesToHHMM(slotStartM); const slotEndActualM = slotStartM + resolutionMins; let sumSlot=0, cntSlot=0; let origTSSlot:string|null=null;
    if (hasValid) { for (let m=slotStartM; m < slotEndActualM; m++) { if (rawMap.has(m)) { const dM=rawMap.get(m)!; sumSlot+=dM.sumI; cntSlot+=dM.cnt; if(!origTSSlot) origTSSlot=dM.firstTS; }}}
    let actInt:number, col:string, lbl:string, txtCol:string|undefined, isPad:boolean, finalOrigTS:string;
    if (cntSlot>0) { actInt=Math.min(1,Math.max(0,sumSlot/cntSlot)); const cfg=getColorAndLabelConfig(actInt,colorConf); col=cfg.color; lbl=cfg.label; txtCol=cfg.textColor; isPad=false; finalOrigTS=origTSSlot||`ACTUAL_${slotTimeS}`; }
    else { actInt=0; col=zC; lbl=zL; txtCol=zT; isPad=true; finalOrigTS=`PAD_${slotTimeS}`; }
    const dispInt = actInt * (1 - MIN_BAR_VISUAL_INTENSITY) + MIN_BAR_VISUAL_INTENSITY;
    finalPoints.push({ index: pIdx, originalTimestamp: finalOrigTS, time: slotTimeS, actualIntensity: actInt, displayIntensity: dispInt, color: col, label: lbl, textColor: txtCol, isPadding: isPad }); pIdx++;
  }
  if (startM <= endM) {
    let actEnd=0, colEnd=zC, lblEnd=zL, txtColEnd=zT, isPadEnd=true, finalOrigTSEnd=`PAD_END_${viewEndTime}`;
    if (hasValid && rawMap.has(endM)) { const dEnd=rawMap.get(endM)!; actEnd=Math.min(1,Math.max(0, dEnd.sumI/dEnd.cnt)); const cfg=getColorAndLabelConfig(actEnd,colorConf); colEnd=cfg.color; lblEnd=cfg.label; txtColEnd=cfg.textColor; isPadEnd=false; finalOrigTSEnd=dEnd.firstTS; }
    const dispIntEnd = actEnd * (1 - MIN_BAR_VISUAL_INTENSITY) + MIN_BAR_VISUAL_INTENSITY;
    if (finalPoints.length === 0 || finalPoints[finalPoints.length - 1].time !== viewEndTime) { finalPoints.push({ index: pIdx, originalTimestamp: finalOrigTSEnd, time: viewEndTime, actualIntensity: actEnd, displayIntensity: dispIntEnd, color: colEnd, label: lblEnd, textColor: txtColEnd, isPadding: isPadEnd }); }
  }
  if (startM > endM) return [{ index: 0, originalTimestamp: `PAD_INVALID_${viewStartTime}`, time: viewStartTime, actualIntensity: 0, displayIntensity: MIN_BAR_VISUAL_INTENSITY, color: zC, label: zL, textColor: zT, isPadding: true }];
  return finalPoints;
};

const findDataPointByTimeOrIndex = (targetIndex: number | null, targetMinute: number | null, chartData: TransformedDataPoint[]): TransformedDataPoint | undefined => {
  if (!chartData||chartData.length===0)return undefined; if(targetIndex!==null && targetIndex>=0 && targetIndex<chartData.length) return chartData[targetIndex];
  if(targetMinute!==null){ let closeP=chartData[0]; let minD=Infinity; for(const p of chartData){ const pMin=timeToMinutes(p.time); const d=Math.abs(pMin-targetMinute); if(d<minD){minD=d; closeP=p;}else if(d===minD && pMin<timeToMinutes(closeP.time))closeP=p;} return closeP; } return undefined;
};

const getXPositionForIndex = (index: number, chartWidth: number, dataLength: number, leftMargin: number, rightMargin: number): number => {
  if (dataLength <= 1) return leftMargin + (chartWidth - leftMargin - rightMargin) / 2; const plotW = chartWidth - leftMargin - rightMargin; return leftMargin + (index / (dataLength - 1)) * plotW;
};

const formatTimeTick = (time: string): string => { try { return String(parseInt(time.split(':')[0], 10)); } catch { return ''; } };

const LoadingShimmer = ({ timeTicks }: { timeTicks: string[] }) => (
  <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ flexGrow: 1, width: '100%', position: 'relative', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%)', animation: 'shimmer 1.5s infinite', '@keyframes shimmer': { '0%': { transform: 'translateX(-100%)' }, '100%': { transform: 'translateX(100%)' } } }} />
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: `0 ${CHART_MARGIN_RIGHT}px 0 ${CHART_MARGIN_LEFT}px`, height: `${GLOBAL_X_AXIS_AREA_HEIGHT_PX}px`, alignItems: 'flex-end', mt: 1 }}>
      {timeTicks.map((tick, idx) => (<Typography key={idx} sx={{ fontSize: `${GLOBAL_X_AXIS_FONT_SIZE_PX}px`, fontFamily: "'Roboto', sans-serif", color: '#333' }}>{tick}</Typography>))}
    </Box>
  </Box>
);
const NoDataDisplay = () => (<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Roboto, sans-serif', color: '#777' }}>No data available</Box>);

interface CustomTooltipContentProps {
  activeData?: TransformedDataPoint; displayTime: string; dynamicStalkHeight: number; effectiveLabel: string; mode: 'full' | 'compact';
}
const CustomTooltipContent = React.memo(({ activeData, displayTime, dynamicStalkHeight, effectiveLabel, mode }: CustomTooltipContentProps) => {
    if (!activeData) return null; const data = activeData; const intensityPercent = Math.round(data.actualIntensity * 100); const barColor = data.color;
    const autoTextColor = parseInt(barColor.substring(1,3),16)*0.299 + parseInt(barColor.substring(3,5),16)*0.587 + parseInt(barColor.substring(5,7),16)*0.114 < 128 ? '#FFFFFF' : '#000000';
    const pillTextColor = data.textColor || autoTextColor; const stalkHeight = Math.max(0, dynamicStalkHeight);
    return (
      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', pointerEvents:'none', width:'auto' }}>
        {/* MODIFICATION 1: Conditionally show time in tooltip based on mode */}
        {mode !== 'compact' && (
            <Typography sx={{ fontSize:`${GLOBAL_TOOLTIP_PILL_TIME_FONT_SIZE_PX}px`, color:'#000000', mb:0.2, opacity:0.9 }}>{displayTime}</Typography>
        )}
        <Box sx={{ backgroundColor:barColor, color:pillTextColor, padding:`${TOOLTIP_PILL_VERTICAL_PADDING_PX}px 12px`, borderRadius:'16px', boxShadow:'0 2px 8px rgba(0,0,0,0.2)', textAlign:'center', fontSize:`${GLOBAL_TOOLTIP_PILL_FONT_SIZE_PX}px`, lineHeight:1.2, whiteSpace:'nowrap' }}>
          {effectiveLabel} {mode === 'full' && `(${intensityPercent}%)`}
        </Box>
        {stalkHeight > 0 && (<Box sx={{ width:`${TOOLTIP_STALK_WIDTH_PX}px`, height:`${stalkHeight}px`, backgroundColor:barColor, marginTop:'-1px' }} />)}
      </Box>
    );
});

const IntensityTimeline = ({
  data,
  googlePlaceId,
  loading = false,
  onTooltipActive,
  mode = 'full',
  startTime: propStartTime = "00:00",
  endTime: propEndTime = "23:59",
  resolutionMinutes: propResolutionMinutes = 15,
  sunriseTime: rawSunriseTime,
  sunsetTime: rawSunsetTime,
  duskDawnDurationMinutes = 30,
  colorConfig = DEFAULT_COLOR_CONFIG,
  tickIntervalHours = 4,
  barStrokeWidth = 1,
}: IntensityTimelineProps) => {

  const isCompactMode = mode === 'compact';
  const currentStartTime = isCompactMode ? "09:00" : propStartTime;
  const currentEndTime = isCompactMode ? "22:00" : propEndTime;
  const currentResolutionMinutes = isCompactMode ? 30 : propResolutionMinutes;
  
  // Mode-dependent layout constants
  const CHART_TOP_MARGIN_MODE = isCompactMode ? 15 : BASE_CHART_TOP_MARGIN;
  const TOOLTIP_OFFSET_ABOVE_CHART_AREA_PX_MODE = isCompactMode ? -15 : BASE_TOOLTIP_OFFSET_ABOVE_CHART_AREA_PX;
  const INDICATOR_CIRCLE_SIZE_PX_MODE = isCompactMode ? 10 : BASE_INDICATOR_CIRCLE_SIZE_PX;
  const INDICATOR_CIRCLE_BORDER_PX_MODE = isCompactMode ? 2 : BASE_INDICATOR_CIRCLE_BORDER_PX;
  const INDICATOR_CIRCLE_Y_OFFSET_PX_MODE = isCompactMode ? -5 : BASE_INDICATOR_CIRCLE_Y_OFFSET_PX;

  // MODIFICATION 2: Adjust TOOLTIP_APPROX_PILL_HEIGHT_MODE based on whether time is shown in pill
  const TOOLTIP_APPROX_PILL_HEIGHT_MODE = useMemo(() => {
    let height = GLOBAL_TOOLTIP_PILL_FONT_SIZE_PX + (2 * TOOLTIP_PILL_VERTICAL_PADDING_PX) + 4; // Base: label + padding
    if (mode !== 'compact') { // Add time height only if not compact (time is shown in the pill)
        height += GLOBAL_TOOLTIP_PILL_TIME_FONT_SIZE_PX;
    }
    return height;
  }, [mode]); // Depends on mode

  const rootContainerRef = useRef<HTMLDivElement>(null);
  const chartWrapperRef = useRef<HTMLDivElement>(null); // Ref for the Box wrapping ResponsiveContainer
  const [containerDimensions, setContainerDimensions] = useState({ height: 150, width: 300 });
  const [activeTooltipInfo, setActiveTooltipInfo] = useState<ActiveTooltipInfo | null>(null);
  const [isInteracting, setIsInteracting] = useState(false);
  const [nowTrigger, setNowTrigger] = useState(0);
  
  const chartData = useMemo(() => processChartData(data, googlePlaceId, currentStartTime, currentEndTime, currentResolutionMinutes, colorConfig),
    [data, googlePlaceId, currentStartTime, currentEndTime, currentResolutionMinutes, colorConfig]);

  useEffect(() => { // Size observer
    const RObserver = new ResizeObserver(() => { if (rootContainerRef.current) setContainerDimensions({ height: rootContainerRef.current.clientHeight, width: rootContainerRef.current.clientWidth }); });
    const currentRef = rootContainerRef.current; if (currentRef) { RObserver.observe(currentRef); setContainerDimensions({ height: currentRef.clientHeight, width: currentRef.clientWidth }); } return () => { if (currentRef) RObserver.unobserve(currentRef); };
  }, []);

  useEffect(() => { // "Now" timer
    const timer = setInterval(() => setNowTrigger(t => t + 1), 60000); return () => clearInterval(timer);
  }, []);

  const parseSunTimeForBoundaries = (timeStr: string | undefined): number => { // Helper for sunrise/sunset
    if (!timeStr) return -1; const p=timeStr.split(':'); if(p.length<2)return -1; const h=parseInt(p[0],10),m=parseInt(p[1],10);let s=0;if(p.length>2)s=parseInt(p[2].split('.')[0],10); if(isNaN(h)||isNaN(m)||isNaN(s))return -1; return h*60+m+(s/60);
  };
  const sunriseMinutes = useMemo(() => parseSunTimeForBoundaries(rawSunriseTime), [rawSunriseTime]);
  const sunsetMinutes = useMemo(() => parseSunTimeForBoundaries(rawSunsetTime), [rawSunsetTime]);

  // Determines the correct label based on time and intensity
  const getEffectiveLabel = useCallback((point: TransformedDataPoint | undefined, currentTimeMinutes: number): string => {
    if (!point) return "No Data";
    const isDay = sunriseMinutes !== -1 && sunsetMinutes !== -1 && currentTimeMinutes >= sunriseMinutes && currentTimeMinutes <= sunsetMinutes;
    const isDawn = sunriseMinutes !== -1 && currentTimeMinutes >= sunriseMinutes && currentTimeMinutes < sunriseMinutes + duskDawnDurationMinutes;
    const isDusk = sunsetMinutes !== -1 && currentTimeMinutes <= sunsetMinutes && currentTimeMinutes > sunsetMinutes - duskDawnDurationMinutes;
    if (isDawn) return "Dawn"; if (isDusk) return "Dusk";
    if (point.actualIntensity === 0) return isDay ? "Full Shade" : "Night";
    return point.label; // Returns "Partial Shade", "Mostly Sun", "Full Sun" etc. from config
  }, [sunriseMinutes, sunsetMinutes, duskDawnDurationMinutes]);

  const xAxisTicks = useMemo(() => { // Calculate X-axis tick indices
    if (chartData.length === 0) return [];
    const startM = timeToMinutes(currentStartTime); const endM = timeToMinutes(currentEndTime); const indices: number[] = [0];
    const firstHour = Math.ceil(startM / 60);
    for (let h = firstHour; h * 60 < endM; h++) { if (h % tickIntervalHours === 0) { const idx = Math.round((h * 60 - startM) / currentResolutionMinutes); if (idx > 0 && idx < chartData.length - 1 && !indices.includes(idx)) indices.push(idx); } }
    if (chartData.length > 1 && !indices.includes(chartData.length - 1)) indices.push(chartData.length - 1);
    return Array.from(new Set(indices)).sort((a, b) => a - b); // Use Array.from for Set iteration
  }, [chartData, currentStartTime, currentEndTime, tickIntervalHours, currentResolutionMinutes]);

  const tooltipPillScreenTop = -TOOLTIP_APPROX_PILL_HEIGHT_MODE - TOOLTIP_OFFSET_ABOVE_CHART_AREA_PX_MODE;
  const plotAreaClientWidth = useMemo(() => Math.max(0, containerDimensions.width - CHART_MARGIN_LEFT - CHART_MARGIN_RIGHT), [containerDimensions.width]);
  const plotAreaClientHeight = useMemo(() => Math.max(0, containerDimensions.height - CHART_TOP_MARGIN_MODE - CHART_BOTTOM_MARGIN - GLOBAL_X_AXIS_AREA_HEIGHT_PX), [containerDimensions.height, CHART_TOP_MARGIN_MODE]);
  const finalStalkHeight = useMemo(() => { // Calculate tooltip stalk height
    const pillBottomY = tooltipPillScreenTop + TOOLTIP_APPROX_PILL_HEIGHT_MODE;
    const circleCenterY = containerDimensions.height - GLOBAL_X_AXIS_AREA_HEIGHT_PX - (INDICATOR_CIRCLE_SIZE_PX_MODE / 2) + INDICATOR_CIRCLE_Y_OFFSET_PX_MODE;
    return Math.max(0, circleCenterY - pillBottomY);
  }, [containerDimensions.height, tooltipPillScreenTop, TOOLTIP_APPROX_PILL_HEIGHT_MODE, INDICATOR_CIRCLE_SIZE_PX_MODE, INDICATOR_CIRCLE_Y_OFFSET_PX_MODE]);

  // Memoized component for dawn/dusk/night shading
  const CustomReferenceAreas = React.memo(({ sTime, eTime, srTime, ssTime, dddMins, cWidth, cHeight }: { sTime: string, eTime: string, srTime?: string, ssTime?: string, dddMins: number, cWidth: number, cHeight: number }) => {
    const startM = useMemo(() => timeToMinutes(sTime), [sTime]); const endM = useMemo(() => timeToMinutes(eTime), [eTime]);
    const totalMView = useMemo(() => Math.max(1, endM - startM), [endM, startM]);
    const currentSrM = useMemo(() => parseSunTimeForBoundaries(srTime), [srTime]); const currentSsM = useMemo(() => parseSunTimeForBoundaries(ssTime), [ssTime]);
    const timeToX = useCallback((tM: number): number => { if (cWidth <= 0) return 0; return Math.max(0, Math.min(cWidth, ((tM - startM) / totalMView) * cWidth)); }, [cWidth, startM, totalMView]);
    const areas = useMemo(() => {
      if (cWidth <= 0 || cHeight <= 0) return []; const res = [];
      const effSr = currentSrM !== -1 ? currentSrM : (startM < 360 ? 360 : -1); const effSs = currentSsM !== -1 ? currentSsM : (endM > 1260 ? 1260 : -1);
      if (effSr > startM) { const x1=timeToX(startM), x2=timeToX(Math.min(effSr,endM)); if(x2>x1) res.push(<rect key="nb" x={x1} y={0} width={x2-x1} height={cHeight} fill="black" fillOpacity={0.2}/>); }
      if (effSr !== -1 && effSr < endM && effSr + dddMins > startM) { const x1=timeToX(Math.max(startM,effSr)), x2=timeToX(Math.min(endM,effSr+dddMins)); if(x2>x1) res.push(<rect key="dwn" x={x1} y={0} width={x2-x1} height={cHeight} fill="#A9A9A9" fillOpacity={0.2}/>); }
      if (effSs !== -1 && effSs > startM && effSs - dddMins < endM) { const x1=timeToX(Math.max(startM,effSs-dddMins)), x2=timeToX(Math.min(endM,effSs)); if(x2>x1) res.push(<rect key="dsk" x={x1} y={0} width={x2-x1} height={cHeight} fill="#A9A9A9" fillOpacity={0.2}/>); }
      if (effSs < endM && effSs !== -1) { const x1=timeToX(Math.max(startM,effSs)), x2=timeToX(endM); if(x2>x1) res.push(<rect key="na" x={x1} y={0} width={x2-x1} height={cHeight} fill="black" fillOpacity={0.2}/>); }
      return res;
    }, [cHeight, cWidth, dddMins, endM, startM, currentSrM, currentSsM, timeToX]);
    if (cWidth <= 0 || cHeight <= 0) return null;
    return (<svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 0 }} children={areas}/>);
  });

  const viewDurationMinutes = useMemo(() => Math.max(1, timeToMinutes(currentEndTime) - timeToMinutes(currentStartTime)), [currentStartTime, currentEndTime]);

  // Updates tooltip based on interaction coordinate (relative to chartWrapperRef)
  const updateTooltipFromCoord = useCallback((xCoordRelativeToWrapper: number) => {
    if (!chartWrapperRef.current || chartData.length === 0 || plotAreaClientWidth <= 0) return;

    const mouseXInPlot = xCoordRelativeToWrapper - CHART_MARGIN_LEFT;

    if (mouseXInPlot < 0 || mouseXInPlot > plotAreaClientWidth) {
        if (isInteracting) { setIsInteracting(false); if (onTooltipActive) onTooltipActive(false); }
        return;
    }

    if (!isInteracting) setIsInteracting(true); // Mark as interacting

    const relativePosInPlot = Math.max(0, Math.min(1, mouseXInPlot / plotAreaClientWidth));
    const numIntervals = chartData.length > 1 ? chartData.length -1 : 1;
    const estimatedIndex = Math.round(relativePosInPlot * numIntervals);
    const closestIndex = Math.max(0, Math.min(estimatedIndex, chartData.length - 1));
    
    const pointForTooltip = chartData[closestIndex];
    // Calculate tooltip's X position based on the *actual* data point's index for snapping
    const xPos = getXPositionForIndex(closestIndex, containerDimensions.width, chartData.length, CHART_MARGIN_LEFT, CHART_MARGIN_RIGHT);
    
    const viewStartMins = timeToMinutes(currentStartTime);
    // Calculate the displayed time based on the *mouse position* for smooth feedback
    const hoveredMinutes = viewStartMins + (relativePosInPlot * viewDurationMinutes);
    const displayTime = minutesToHHMM(hoveredMinutes);

    if (pointForTooltip) {
      setActiveTooltipInfo({ point: pointForTooltip, xPos: xPos, displayTime: displayTime });
      if (onTooltipActive) onTooltipActive(true, pointForTooltip);
    }
  }, [chartData, plotAreaClientWidth, containerDimensions.width, currentStartTime, viewDurationMinutes, onTooltipActive, isInteracting]);


  useEffect(() => { // "Now" indicator effect
    if (chartData.length === 0 || plotAreaClientWidth <= 0 || isInteracting) return; // Don't update if user is interacting
    const now = new Date(); const nowMins = dateToMinutes(now);
    const viewStartMins = timeToMinutes(currentStartTime); const viewEndMins = timeToMinutes(currentEndTime);
    let pointForNow: TransformedDataPoint | undefined; let xPosNow: number; let displayNowTime = minutesToHHMM(nowMins);

    if (nowMins >= viewStartMins && nowMins <= viewEndMins) {
        const targetIndex = Math.round((nowMins - viewStartMins) / currentResolutionMinutes);
        pointForNow = chartData[Math.min(targetIndex, chartData.length - 1)];
        xPosNow = getXPositionForIndex(pointForNow?.index ?? targetIndex, containerDimensions.width, chartData.length, CHART_MARGIN_LEFT, CHART_MARGIN_RIGHT);
    } else if (nowMins < viewStartMins) {
        pointForNow = chartData[0]; xPosNow = CHART_MARGIN_LEFT; displayNowTime = pointForNow?.time || currentStartTime;
    } else {
        pointForNow = chartData[chartData.length - 1]; xPosNow = containerDimensions.width - CHART_MARGIN_RIGHT; displayNowTime = pointForNow?.time || currentEndTime;
    }
    if (pointForNow) {
      setActiveTooltipInfo({ point: pointForNow, xPos: xPosNow, displayTime: displayNowTime });
      if (onTooltipActive && !isInteracting) onTooltipActive(true, pointForNow);
    }
  }, [chartData, plotAreaClientWidth, containerDimensions.width, currentResolutionMinutes, currentStartTime, currentEndTime, nowTrigger, isInteracting, onTooltipActive]);

  // Hover handler (desktop) - uses Recharts coordinate
  const handleChartMouseMoveInternal = (e: any) => {
    if (e && e.activeCoordinate !== undefined) {
        if (chartWrapperRef.current) {
            updateTooltipFromCoord(e.activeCoordinate.x);
        }
    }
  };

  // Click/Tap handler (mobile/desktop) - uses viewport coordinate
  const handleChartClickInternal = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); 
    if (chartWrapperRef.current) {
        const chartRect = chartWrapperRef.current.getBoundingClientRect();
        const xCoordRelativeToWrapper = e.clientX - chartRect.left;
        updateTooltipFromCoord(xCoordRelativeToWrapper);
    }
  };

  // Mouse leave handler
  const handleChartMouseLeaveInternal = () => {
    setIsInteracting(false); 
    if (onTooltipActive) onTooltipActive(false);
  };


  if (loading) return <Box sx={{height: '100%', width: '100%'}}><LoadingShimmer timeTicks={['00','06','12','18','23']} /></Box>;
  if (chartData.length === 0 && !loading) return <Box sx={{height: '100%', width: '100%'}}><NoDataDisplay /></Box>;
  
  const currentTooltipPointTimeMinutes = activeTooltipInfo?.point ? timeToMinutes(activeTooltipInfo.point.time) : -1;
  const effectiveTooltipLabel = getEffectiveLabel(activeTooltipInfo?.point, currentTooltipPointTimeMinutes);

  return (
    <Box
        data-testid={'intensity-timeline-root'}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          border: 'none !important',
        }}
        ref={rootContainerRef} 
        onClick={handleChartClickInternal} // Add click listener to the root
    >
      {/* Reference Areas absolutely positioned within the root, sized to plot area */}
      {plotAreaClientWidth > 0 && plotAreaClientHeight > 0 && (
        <Box sx={{ position: 'absolute', top: `${CHART_TOP_MARGIN_MODE}px`, left: `${CHART_MARGIN_LEFT}px`, width: `${plotAreaClientWidth}px`, height: `${plotAreaClientHeight}px`, pointerEvents: 'none', zIndex: 0 }}>
            <CustomReferenceAreas sTime={currentStartTime} eTime={currentEndTime} srTime={rawSunriseTime} ssTime={rawSunsetTime} dddMins={duskDawnDurationMinutes} cWidth={plotAreaClientWidth} cHeight={plotAreaClientHeight}/>
        </Box>
      )}
      {/* Tooltip absolutely positioned within the root */}
      {activeTooltipInfo && activeTooltipInfo.point && (
        <Box sx={{ position: 'absolute', top: `${tooltipPillScreenTop}px`, left: `${activeTooltipInfo.xPos}px`, transform: 'translateX(-50%)', zIndex: 100, pointerEvents: 'none', transition: isInteracting ? 'none' : 'left 0.1s ease-out' }}>
          <CustomTooltipContent activeData={activeTooltipInfo.point} displayTime={activeTooltipInfo.displayTime} dynamicStalkHeight={finalStalkHeight} effectiveLabel={effectiveTooltipLabel} mode={mode}/>
        </Box>
      )}
      {/* Chart Container absolutely positioned within the root */}
      <Box sx={{ width: '100%', height: `${containerDimensions.height}px`, position: 'absolute', top: 0, left: 0, zIndex: 1 }} ref={chartWrapperRef}>
        {containerDimensions.height > GLOBAL_X_AXIS_AREA_HEIGHT_PX + CHART_TOP_MARGIN_MODE + CHART_BOTTOM_MARGIN && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: CHART_TOP_MARGIN_MODE, right: CHART_MARGIN_RIGHT, bottom: CHART_BOTTOM_MARGIN, left: CHART_MARGIN_LEFT }} barCategoryGap={`${BAR_CATEGORY_GAP_PERCENT}%`} barGap={0}
              onMouseMove={handleChartMouseMoveInternal} 
              onMouseLeave={handleChartMouseLeaveInternal}
            >
              <CartesianGrid horizontal={false} stroke="#eee" strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" type="number" domain={[0, chartData.length > 0 ? chartData.length - 1 : 0]} ticks={xAxisTicks}
                tickFormatter={(idx:number) => chartData[idx] ? formatTimeTick(chartData[idx].time) : ""}
                tick={{ fontSize: GLOBAL_X_AXIS_FONT_SIZE_PX, fill: '#333' }}
                axisLine={false} tickLine={false} interval={0} height={GLOBAL_X_AXIS_AREA_HEIGHT_PX}/>
              <YAxis domain={[0, 1]} hide yAxisId="intensity" />
              <Bar dataKey="displayIntensity" yAxisId="intensity" radius={[BAR_TOP_RADIUS_PX, BAR_TOP_RADIUS_PX, 0, 0]} isAnimationActive={false}>
                {chartData.map((entry) => (<Cell key={`cell-${entry.index}-${entry.originalTimestamp}`} fill={entry.color} stroke={"transparent"} strokeWidth={barStrokeWidth}/>))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
      {/* Indicator Circle absolutely positioned within the root */}
      {activeTooltipInfo && activeTooltipInfo.point && (
        <Box sx={{
          position: 'absolute',
          top: `${containerDimensions.height - GLOBAL_X_AXIS_AREA_HEIGHT_PX - (INDICATOR_CIRCLE_SIZE_PX_MODE / 2) + INDICATOR_CIRCLE_Y_OFFSET_PX_MODE}px`,
          left: `${activeTooltipInfo.xPos}px`,
          transform: 'translateX(-50%)',
          width: `${INDICATOR_CIRCLE_SIZE_PX_MODE}px`,
          height: `${INDICATOR_CIRCLE_SIZE_PX_MODE}px`,
          backgroundColor: activeTooltipInfo.point.color,
          borderRadius: '50%',
          border: `${INDICATOR_CIRCLE_BORDER_PX_MODE}px solid white`,
          zIndex: 101, pointerEvents: 'none',
          transition: isInteracting ? 'none' : 'left 0.1s ease-out',
        }}/>
      )}

      {/* MODIFICATION 3: Add time display below chart for compact mode */}
      {mode === 'compact' && activeTooltipInfo && activeTooltipInfo.point && (
        <Typography
          sx={{
            position: 'absolute',
            // containerDimensions.height is height of chartWrapperRef (which holds the BarChart)
            // CHART_BOTTOM_MARGIN is the BarChart's own bottom margin.
            // X-axis labels are rendered above this margin.
            // This positions the text just below where the BarChart's visual area (including X-axis labels) ends.
            top: `${containerDimensions.height - CHART_BOTTOM_MARGIN + 5}px`, // Adjust '2' for desired vertical spacing
            left: `${activeTooltipInfo.xPos}px`,
            transform: 'translateX(-50%)',
            fontSize: `${GLOBAL_TOOLTIP_PILL_TIME_FONT_SIZE_PX}px`,
            color: '#333', // Or your preferred color
            zIndex: 50, // Adjust as needed, e.g., to be above reference areas but below tooltip/indicator
            pointerEvents: 'none',
            transition: isInteracting ? 'none' : 'left 0.1s ease-out', // Match tooltip/indicator transition
            fontFamily: "'Roboto', sans-serif", // Optional: ensure consistent font
          }}
        >
          {activeTooltipInfo.displayTime}
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(IntensityTimeline);